import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { messagingService } from '../../../lib/services';

const DirectMessaging = ({ threads, currentUserId, onRefresh }) => {
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleThreadSelect = async (thread) => {
    setSelectedThread(thread);
    setLoading(true);
    const { data } = await messagingService?.getThreadMessages(thread?.id);
    if (data) {
      setMessages(data);
    }
    await messagingService?.markThreadAsRead(thread?.id);
    setLoading(false);
    onRefresh();
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage?.trim() || !selectedThread) return;

    const { data, error } = await messagingService?.sendMessage(selectedThread?.id, newMessage);
    if (!error && data) {
      setMessages(prev => [...prev, data]);
      setNewMessage('');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    return date?.toLocaleDateString();
  };

  const getOtherParticipants = (thread) => {
    return thread?.participants
      ?.filter(p => p?.user_id !== currentUserId)
      ?.map(p => p?.user_profile?.full_name)
      ?.join(', ') || 'Unknown';
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Threads List */}
      <div className="lg:col-span-1 space-y-3">
        {threads?.length === 0 ? (
          <div className="bg-card rounded-xl p-8 shadow-md border border-border text-center">
            <Icon name="MessageSquare" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No messages yet</p>
          </div>
        ) : (
          threads?.map((thread) => {
            const lastMessage = thread?.messages?.[thread?.messages?.length - 1];
            const isUnread = thread?.participants?.find(p => p?.user_id === currentUserId)?.last_read_at
              ? new Date(thread?.updated_at) > new Date(thread?.participants?.find(p => p?.user_id === currentUserId)?.last_read_at)
              : true;

            return (
              <button
                key={thread?.id}
                onClick={() => handleThreadSelect(thread)}
                className={`
                  w-full text-left bg-card rounded-xl p-4 shadow-md border transition-all
                  ${selectedThread?.id === thread?.id
                    ? 'border-primary ring-2 ring-primary/20' :'border-border hover:border-primary/50'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-foreground text-sm truncate">
                        {getOtherParticipants(thread)}
                      </h3>
                      {isUnread && (
                        <span className="w-2 h-2 bg-primary rounded-full"></span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {lastMessage?.content || thread?.subject}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatDate(thread?.updated_at)}
                    </p>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      {/* Messages View */}
      <div className="lg:col-span-2">
        {selectedThread ? (
          <div className="bg-card rounded-xl shadow-md border border-border h-[600px] flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-border">
              <h3 className="font-semibold text-foreground">{selectedThread?.subject}</h3>
              <p className="text-sm text-muted-foreground">
                {getOtherParticipants(selectedThread)}
              </p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin" />
                </div>
              ) : (
                messages?.map((message) => {
                  const isOwn = message?.sender_id === currentUserId;
                  return (
                    <div
                      key={message?.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isOwn ? 'order-2' : 'order-1'}`}>
                        <div
                          className={`rounded-lg p-3 ${
                            isOwn
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-foreground'
                          }`}
                        >
                          {!isOwn && (
                            <p className="text-xs font-medium mb-1">
                              {message?.sender?.full_name}
                            </p>
                          )}
                          <p className="text-sm">{message?.content}</p>
                        </div>
                        <p className={`text-xs text-muted-foreground mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                          {formatDate(message?.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-border">
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e?.target?.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <Button
                  type="submit"
                  disabled={!newMessage?.trim()}
                  iconName="Send"
                >
                  Send
                </Button>
              </form>
            </div>
          </div>
        ) : (
          <div className="bg-card rounded-xl p-12 shadow-md border border-border h-full flex flex-col items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
              <Icon name="MessageSquare" size={40} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No Conversation Selected
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Select a conversation to view messages
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectMessaging;